import axios from "axios";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { FaThumbsUp } from "react-icons/fa";
import { TbCreditCardPay } from "react-icons/tb";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

export default function DriverPayments() {
   const [payments, setPayments] = useState([]);
   const [filteredPayments, setFilteredPayments] = useState([]);
   const [searchTerm, setSearchTerm] = useState("");

   // Pagination
   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 7;

   useEffect(() => {
      fetchPayments();
   }, []);

   useEffect(() => {
      handleSearch();
   }, [searchTerm, payments]);

   const fetchPayments = async () => {
      try {
         const rc_id = sessionStorage.getItem("rc_id");
         const center_name = sessionStorage.getItem("center_name");

         if (!rc_id || !center_name) {
            toast.error("❌ Missing Redemption Center details. Please log in again.");
            return;
         }

         const res = await axios.get(`/auth/driver-payments`, {
            params: { rc_id },
         });

         setPayments(res.data);
         setFilteredPayments(res.data);
      } catch (err) {
         console.error("Error fetching driver payments:", err);
         toast.error("❌ Failed to load driver payments");
      }
   };
   const exportToExcel = () => {
      if (payments.length === 0) {
         toast.warning("No data to export!");
         return;
      }

      const exportData = payments.map((p, index) => ({
         "S.No": index + 1,
         "Driver ID": p.driver_id || "-",
         "Driver Name": p.driver_name || "-",
         "Amount (₹)": p.amount || "-",
         "Payment Method": p.payment_method || "-",
         "Pickup Status": p.status || "-",
         "Payment Status": p.payment_status || "-",
         "RC ID": p.rc_id || "-",
         "Center Name": p.center_name || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Driver Payments");

      // Auto column width
      const columnWidths = Object.keys(exportData[0]).map((key) => ({
         wch: Math.max(
            key.length,
            ...exportData.map((row) => (row[key] ? row[key].toString().length : 0))
         ) + 2,
      }));
      worksheet["!cols"] = columnWidths;

      // Freeze header row
      worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

      // Header styling: green background + white text + bold
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let C = range.s.c; C <= range.e.c; ++C) {
         const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
         if (!worksheet[cellRef]) continue;
         worksheet[cellRef].s = {
            font: { bold: true, color: { rgb: "FFFFFFFF" } }, // White text
            fill: { fgColor: { rgb: "FF2E7D32" } }, // Green background
            alignment: { horizontal: "center", vertical: "center" },
         };
      }

      // Align other rows
      for (let R = 1; R <= range.e.r; ++R) {
         for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            if (worksheet[cellRef]) {
               worksheet[cellRef].s = {
                  alignment: { vertical: "center", horizontal: "center" },
               };
            }
         }
      }

      // Export
      const excelBuffer = XLSX.write(workbook, {
         bookType: "xlsx",
         type: "array",
         cellStyles: true,
      });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(blob, `Driver_Payments_${new Date().toISOString().split("T")[0]}.xlsx`);
   };

   const handleSearch = () => {
      const lower = searchTerm.toLowerCase();
      const filtered = payments.filter(p =>
         p.driver_name?.toLowerCase().includes(lower) ||
         p.driver_id?.toString().toLowerCase().includes(lower) ||
         p.payment_status?.toLowerCase().includes(lower) ||
         (p.date && new Date(p.date).toLocaleDateString().includes(lower))
      );
      setFilteredPayments(filtered);
      setCurrentPage(1);
   };

   // Pagination logic
   const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
   const startIndex = (currentPage - 1) * itemsPerPage;
   const currentPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

   const getPageNumbers = () => {
      const pages = [];
      if (totalPages <= 6) {
         for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
         if (currentPage <= 3) {
            pages.push(1, 2, 3, 4, "...", totalPages);
         } else if (currentPage >= totalPages - 2) {
            pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
         } else {
            pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
         }
      }
      return pages;
   };

   return (
      <div className="p-4">
         <div className="bg-white shadow-md rounded-2xl p-5 w-full">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
               <h2 className="text-2xl font-bold text-green-800 flex items-center gap-2">
                  <TbCreditCardPay className="text-3xl" /> Driver Payments
               </h2>
               <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-stretch sm:items-center">
                  <input
                     type="text"
                     placeholder="Search by ID, Name, Date, Amount..."
                     value={searchTerm}
                     onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                     }}
                     className="border px-4 py-2 rounded-lg w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                     onClick={exportToExcel}
                     disabled={payments.length === 0}
                     className={`px-4 py-2 rounded-lg transition ${payments.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700 text-white"
                        }`}
                  >
                     Export To Excel
                  </button>

               </div>
            </div>

            <div className="w-full overflow-x-auto">
               <table className="min-w-max w-full border border-gray-200">
                  <thead className="bg-lime-100 text-green-900 uppercase font-semibold">
                     <tr>
                        <th className="px-3 py-4">S.no</th>
                        <th className="px-4 py-4">Driver ID</th>
                        <th className="px-4 py-4">Driver Name</th>
                        <th className="px-4 py-4">Amount</th>
                        <th className="px-4 py-4">Date</th>
                        <th className="px-4 py-4">Payment Method</th>
                        <th className="px-4 py-4">Status</th>
                        <th className="px-4 py-4">Payment Status</th>
                        <th className="px-2 py-1">Actions</th>
                     </tr>
                  </thead>
                  <tbody>
                     {currentPayments.length > 0 ? (
                        currentPayments.map((p, index) => (
                           <tr key={p.pickup_id} className="bg-white border-b hover:bg-gray-50 text-center">
                              <td className="px-3 py-4">{startIndex + index + 1}</td>
                              <td className="px-4 py-4">{p.driver_id}</td>
                              <td className="px-4 py-4">{p.driver_name}</td>
                              <td className="px-4 py-4">{p.amount || ""}</td>
                              <td className="px-4 py-4">{p.date ? new Date(p.date).toLocaleDateString() : ""}</td>
                              <td className="px-4 py-4">{p.payment_method || ""}</td>
                              <td className="px-4 py-4">
                                 <span className="text-xs font-medium px-2 py-1 rounded-lg bg-green-200 text-green-800">
                                    {p.status}
                                 </span>
                              </td>
                              <td className="px-4 py-4">
                                 <span className={`text-xs font-medium px-2 py-1 rounded-lg ${p.payment_status?.toLowerCase() === "completed"
                                    ? "bg-green-200 text-green-800"
                                    : "bg-red-200 text-red-800"
                                    }`}
                                 >
                                    {p.payment_status}
                                 </span>
                              </td>
                              <td className="px-4 py-4 flex justify-center gap-2">
                                 {p.payment_status?.toLowerCase() === "completed" ? (
                                    <span className="flex items-center gap-2 text-green-700 font-semibold">
                                       <FaThumbsUp className="text-green-600" /> Paid
                                    </span>
                                 ) : (
                                    <span className="flex items-center gap-2 text-gray-500">
                                       <FaThumbsUp className="text-gray-500" /> Pending
                                    </span>
                                 )}
                              </td>
                           </tr>
                        ))
                     ) : (
                        <tr>
                           <td colSpan="9" className="text-center py-4">
                              No matching payments found.
                           </td>
                        </tr>
                     )}
                  </tbody>
               </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
               <div className="flex justify-center mt-6 gap-2">
                  <button
                     disabled={currentPage === 1}
                     onClick={() => setCurrentPage(prev => prev - 1)}
                     className="px-3 py-1 border rounded-md disabled:opacity-50"
                  >
                     &lt;
                  </button>

                  {getPageNumbers().map((page, index) =>
                     page === "..." ? (
                        <span key={index} className="px-3 py-1">...</span>
                     ) : (
                        <button
                           key={index}
                           onClick={() => setCurrentPage(page)}
                           className={`px-3 py-1 border rounded-md ${currentPage === page ? "bg-[#a3e635] text-white" : ""}`}
                        >
                           {page}
                        </button>
                     )
                  )}

                  <button
                     disabled={currentPage === totalPages}
                     onClick={() => setCurrentPage(prev => prev + 1)}
                     className="px-3 py-1 border rounded-md disabled:opacity-50"
                  >
                     &gt;
                  </button>
               </div>
            )}
         </div>
      </div>
   );
}
